import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import {
  availableOrthoIndexes,
  buildOrthoIndexExpression,
  buildOrthoIndexPreviewUrl,
  DRONE_INDEX_DEFS,
  DRONE_ORTHO_BANDS,
  rateIndexPlain,
  type DroneIndexDef,
  type DroneIndexKey,
} from '../constants/droneOrthoBands';
import {
  type DroneMissionConfig,
  type DroneMissionKey,
} from '../constants/dronePrescriptionLayers';
import { parseTitilerHistogram, type HistogramBin } from './dronePrescriptionAnalytics';

/** Unwrap field GeoJSON without falling back to mission bbox. */
function extractFieldGeometry(
  fieldGeoJson: Record<string, unknown> | null | undefined
): GeoJSON.Geometry | null {
  if (!fieldGeoJson) return null;
  if (fieldGeoJson.type === 'Feature') {
    return ((fieldGeoJson as GeoJSON.Feature).geometry as GeoJSON.Geometry) ?? null;
  }
  if (
    fieldGeoJson.type === 'Polygon' ||
    fieldGeoJson.type === 'MultiPolygon' ||
    fieldGeoJson.type === 'GeometryCollection'
  ) {
    return fieldGeoJson as GeoJSON.Geometry;
  }
  if (fieldGeoJson.type === 'FeatureCollection') {
    const feat = (fieldGeoJson as GeoJSON.FeatureCollection).features?.[0];
    return feat?.geometry ?? null;
  }
  if (fieldGeoJson.geometry && typeof fieldGeoJson.geometry === 'object') {
    return fieldGeoJson.geometry as GeoJSON.Geometry;
  }
  return null;
}

const TITILER_STATS_MAX_SIZE = 512;
const HISTOGRAM_BINS = 40;

/** NDVI threshold for green canopy cover fraction (illustrative). */
export const CANOPY_COVER_NDVI_THRESHOLD = 0.3;

export type IndexStatResult = {
  key: DroneIndexKey;
  label: string;
  description: string;
  mean: number;
  min: number;
  max: number;
  std: number | null;
  validPercent: number;
  validPixelCount: number;
  histogram: HistogramBin[];
  available: boolean;
  unavailableReason?: string;
};

export type ParameterExtractionResult = {
  canopyCoverPercent: number | null;
  canopyCoverNote: string;
  /** Canopy Height Model — not available without DSM/DTM in current COGs. */
  chmMeters: number | null;
  chmNote: string;
  chlorophyllProxy: number | null;
  chlorophyllProxyLabel: string;
  chlorophyllProxyNote: string;
};

export type DroneOrthoFieldReport = {
  missionKey: DroneMissionKey;
  missionLabel: string;
  farmName: string | null;
  analysisSource: 'orthophoto';
  generatedAt: string;
  fieldAreaHa: number;
  /** Selected field bbox [minX, minY, maxX, maxY] for preview crop. */
  fieldBbox: [number, number, number, number] | null;
  /** TiTiler preview URL of the field-level index map (real drone output). */
  previewUrl: string | null;
  overlap: boolean;
  overlapNote: string;
  indexes: IndexStatResult[];
  parameters: ParameterExtractionResult;
};

export type DroneOrthoAnalysisState =
  | { status: 'idle' }
  | { status: 'no_field' }
  | { status: 'no_overlap'; missionLabel: string }
  | { status: 'loading'; progress: number; total: number }
  | { status: 'loaded'; report: DroneOrthoFieldReport }
  | { status: 'error'; message: string };

function geometryBbox(geometry: GeoJSON.Geometry): [number, number, number, number] | null {
  const coords: number[][] = [];
  const walk = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      coords.push([c[0] as number, c[1] as number]);
      return;
    }
    for (const item of c) walk(item);
  };

  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    walk(geometry.coordinates);
  } else if (geometry.type === 'GeometryCollection') {
    for (const g of geometry.geometries) {
      const b = geometryBbox(g);
      if (b) {
        coords.push([b[0], b[1]], [b[2], b[3]]);
      }
    }
  } else {
    return null;
  }

  if (coords.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of coords) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return [minX, minY, maxX, maxY];
}

export function bboxesOverlap(
  a: [number, number, number, number],
  b: [number, number, number, number]
): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

export function fieldOverlapsDroneMission(
  fieldGeoJson: Record<string, unknown> | null | undefined,
  mission: DroneMissionConfig
): { overlaps: boolean; geometry: GeoJSON.Geometry | null } {
  const geometry = extractFieldGeometry(fieldGeoJson);
  if (!geometry) return { overlaps: false, geometry: null };
  const fb = geometryBbox(geometry);
  if (!fb) return { overlaps: false, geometry: null };
  return { overlaps: bboxesOverlap(fb, mission.bbox), geometry };
}

function geoJsonAreaHa(geometry: GeoJSON.Geometry): number {
  const rings: number[][][] = [];
  if (geometry.type === 'Polygon') {
    rings.push(geometry.coordinates[0]);
  } else if (geometry.type === 'MultiPolygon') {
    for (const poly of geometry.coordinates) rings.push(poly[0]);
  } else {
    return 0;
  }

  let areaM2 = 0;
  for (const ring of rings) {
    if (ring.length < 3) continue;
    const latRad = ring.reduce((sum, c) => sum + (c[1] * Math.PI) / 180, 0) / ring.length;
    const mPerDegLat = 111_320;
    const mPerDegLon = 111_320 * Math.cos(latRad);
    let sum = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      sum += (x2 - x1) * mPerDegLon * (y2 + y1) * mPerDegLat * 0.5;
    }
    areaM2 += Math.abs(sum);
  }
  return areaM2 / 10_000;
}

function pickStatsBand(statsRoot: Record<string, unknown> | undefined): Record<string, unknown> | null {
  if (!statsRoot) return null;
  if (statsRoot.b1 && typeof statsRoot.b1 === 'object') {
    return statsRoot.b1 as Record<string, unknown>;
  }
  // Expression results are keyed by the expression string
  const first = Object.values(statsRoot).find((v) => v && typeof v === 'object') as
    | Record<string, unknown>
    | undefined;
  return first ?? null;
}

async function fetchExpressionStats(
  cogUrl: string,
  expression: string,
  geometry: GeoJSON.Geometry,
  signal?: AbortSignal
): Promise<{
  mean: number;
  min: number;
  max: number;
  std: number | null;
  validPercent: number;
  validPixelCount: number;
  histogram: HistogramBin[];
} | null> {
  const params = new URLSearchParams({
    url: cogUrl,
    expression,
    max_size: String(TITILER_STATS_MAX_SIZE),
    histogram_bins: String(HISTOGRAM_BINS),
  });

  const featureBody = {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry, properties: {} }],
  };

  const res = await fetch(`${DIGISAKA_API_ORIGIN}/api/statistics?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(featureBody),
    signal,
  });

  if (!res.ok) {
    throw new Error(`Index computation failed (${res.status})`);
  }

  const data = (await res.json()) as Record<string, unknown>;
  const feat = (data.features as Record<string, unknown>[] | undefined)?.[0];
  const featProps = (feat?.properties ?? data.properties) as Record<string, unknown> | undefined;
  const statsRoot = featProps?.statistics as Record<string, unknown> | undefined;
  const stats = pickStatsBand(statsRoot);
  if (!stats || stats.mean == null) return null;

  const validPercent = Number(stats.valid_percent ?? 0);
  const validPixelCount = Number(stats.count ?? stats.valid_pixels ?? 0);
  if (validPercent <= 0 && validPixelCount <= 0) return null;

  return {
    mean: Number(stats.mean),
    min: Number(stats.min ?? NaN),
    max: Number(stats.max ?? NaN),
    std: stats.std != null ? Number(stats.std) : null,
    validPercent,
    validPixelCount,
    histogram: parseTitilerHistogram(stats.histogram),
  };
}

function canopyCoverFromNdviHistogram(histogram: HistogramBin[]): number | null {
  const total = histogram.reduce((s, b) => s + b.count, 0);
  if (total <= 0) return null;
  const green = histogram
    .filter((b) => b.value >= CANOPY_COVER_NDVI_THRESHOLD)
    .reduce((s, b) => s + b.count, 0);
  return (green / total) * 100;
}

function buildParameters(indexes: IndexStatResult[]): ParameterExtractionResult {
  const ndvi = indexes.find((i) => i.key === 'ndvi' && i.available);
  const ndre = indexes.find((i) => i.key === 'ndre' && i.available);

  const canopyCoverPercent = ndvi ? canopyCoverFromNdviHistogram(ndvi.histogram) : null;

  let chlorophyllProxy: number | null = null;
  let chlorophyllProxyLabel = 'Leaf greenness';
  let chlorophyllProxyNote =
    'Run Plant greenness (NDVI) or Leaf richness (NDRE) to estimate how green the leaves look.';

  if (ndre) {
    chlorophyllProxy = ndre.mean;
    chlorophyllProxyLabel = 'Leaf richness';
    chlorophyllProxyNote =
      'Higher leaf richness usually means greener leaves. Red areas on the map may need a closer look.';
  } else if (ndvi) {
    chlorophyllProxy = ndvi.mean;
    chlorophyllProxyLabel = 'Plant greenness';
    chlorophyllProxyNote =
      'Higher plant greenness usually means healthier, greener crop cover on your field.';
  }

  return {
    canopyCoverPercent,
    canopyCoverNote:
      canopyCoverPercent != null
        ? 'Share of the field that looks well covered with green plants.'
        : 'Run Plant greenness (NDVI) to estimate how much of the field is covered by plants.',
    chmMeters: null,
    chmNote:
      'Plant height is not available from this drone package yet (needs a special height map).',
    chlorophyllProxy,
    chlorophyllProxyLabel,
    chlorophyllProxyNote,
  };
}

/** Run a single index on the orthophoto for an overlapping field. */
export async function runSingleDroneOrthoIndex(
  mission: DroneMissionConfig,
  fieldGeoJson: Record<string, unknown>,
  farmName: string | null | undefined,
  indexKey: DroneIndexKey,
  signal?: AbortSignal
): Promise<DroneOrthoFieldReport> {
  const { overlaps, geometry } = fieldOverlapsDroneMission(fieldGeoJson, mission);
  if (!overlaps || !geometry) {
    throw new Error('Your field is outside this drone map area.');
  }

  const bands = DRONE_ORTHO_BANDS[mission.key];
  const def = DRONE_INDEX_DEFS.find((d) => d.key === indexKey);
  if (!def) throw new Error('Unknown index.');

  if (def.requiresRedEdge && bands.redEdge == null) {
    throw new Error('Leaf richness (NDRE) is not available for this drone map.');
  }

  const result = await computeOneIndex(mission, bands, def, geometry, signal);
  if (!result.available) {
    throw new Error(result.unavailableReason || 'Could not analyse this field.');
  }

  const fieldBbox = geometryBbox(geometry);
  // Slight pad so the field edge is not clipped in the report preview
  const previewBbox = fieldBbox
    ? ([
        fieldBbox[0] - (fieldBbox[2] - fieldBbox[0]) * 0.08,
        fieldBbox[1] - (fieldBbox[3] - fieldBbox[1]) * 0.08,
        fieldBbox[2] + (fieldBbox[2] - fieldBbox[0]) * 0.08,
        fieldBbox[3] + (fieldBbox[3] - fieldBbox[1]) * 0.08,
      ] as [number, number, number, number])
    : mission.bbox;

  return {
    missionKey: mission.key,
    missionLabel: mission.label,
    farmName: farmName?.trim() || null,
    analysisSource: 'orthophoto',
    generatedAt: new Date().toISOString(),
    fieldAreaHa: geoJsonAreaHa(geometry),
    fieldBbox,
    previewUrl: buildOrthoIndexPreviewUrl(mission.orthoCogUrl, indexKey, bands, {
      bbox: previewBbox,
      maxSize: 720,
    }),
    overlap: true,
    overlapNote: 'Your field is inside this drone map area.',
    indexes: [result],
    parameters: buildParameters([result]),
  };
}

/**
 * Run orthophoto index computation + parameter extraction for a field that overlaps a mission.
 */
export async function runDroneOrthoFieldAnalysis(
  mission: DroneMissionConfig,
  fieldGeoJson: Record<string, unknown>,
  farmName: string | null | undefined,
  opts?: {
    signal?: AbortSignal;
    onProgress?: (done: number, total: number) => void;
    indexes?: DroneIndexKey[];
  }
): Promise<DroneOrthoFieldReport> {
  const { overlaps, geometry } = fieldOverlapsDroneMission(fieldGeoJson, mission);
  if (!overlaps || !geometry) {
    throw new Error('Selected field does not overlap this drone mission extent.');
  }

  const bands = DRONE_ORTHO_BANDS[mission.key];
  const requested = opts?.indexes;
  const defs = availableOrthoIndexes(bands).filter(
    (d) => !requested || requested.includes(d.key)
  );

  const indexes: IndexStatResult[] = [];
  let done = 0;
  const total = defs.length;
  opts?.onProgress?.(0, total);

  for (const def of defs) {
    const result = await computeOneIndex(mission, bands, def, geometry, opts?.signal);
    // Only include indexes that actually computed for this map
    if (result.available) indexes.push(result);
    done += 1;
    opts?.onProgress?.(done, total);
  }

  const order: DroneIndexKey[] = ['ndvi', 'ndre', 'exg', 'savi'];
  indexes.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

  if (indexes.length === 0) {
    throw new Error('No crop checks could be computed for this field on the selected drone map.');
  }

  const fieldBbox = geometryBbox(geometry);
  const primary = indexes[0]?.key ?? 'ndvi';

  return {
    missionKey: mission.key,
    missionLabel: mission.label,
    farmName: farmName?.trim() || null,
    analysisSource: 'orthophoto',
    generatedAt: new Date().toISOString(),
    fieldAreaHa: geoJsonAreaHa(geometry),
    fieldBbox,
    previewUrl: buildOrthoIndexPreviewUrl(mission.orthoCogUrl, primary, bands, {
      bbox: fieldBbox ?? mission.bbox,
      maxSize: 720,
    }),
    overlap: true,
    overlapNote: 'Field geometry intersects the drone mission footprint.',
    indexes,
    parameters: buildParameters(indexes),
  };
}

async function computeOneIndex(
  mission: DroneMissionConfig,
  bands: (typeof DRONE_ORTHO_BANDS)[DroneMissionKey],
  def: DroneIndexDef,
  geometry: GeoJSON.Geometry,
  signal?: AbortSignal
): Promise<IndexStatResult> {
  const expression = buildOrthoIndexExpression(def.key, bands);
  if (!expression) {
    return {
      key: def.key,
      label: def.label,
      description: def.description,
      mean: NaN,
      min: NaN,
      max: NaN,
      std: null,
      validPercent: 0,
      validPixelCount: 0,
      histogram: [],
      available: false,
      unavailableReason: def.requiresRedEdge
        ? 'RedEdge band not present in this orthophoto.'
        : 'Expression not available for this mission.',
    };
  }

  try {
    const stats = await fetchExpressionStats(mission.orthoCogUrl, expression, geometry, signal);
    if (!stats) {
      return {
        key: def.key,
        label: def.label,
        description: def.description,
        mean: NaN,
        min: NaN,
        max: NaN,
        std: null,
        validPercent: 0,
        validPixelCount: 0,
        histogram: [],
        available: false,
        unavailableReason: 'No valid pixels for this index inside the field.',
      };
    }
    return {
      key: def.key,
      label: def.label,
      description: def.description,
      mean: stats.mean,
      min: stats.min,
      max: stats.max,
      std: stats.std,
      validPercent: stats.validPercent,
      validPixelCount: stats.validPixelCount,
      histogram: stats.histogram,
      available: true,
    };
  } catch (err) {
    return {
      key: def.key,
      label: def.label,
      description: def.description,
      mean: NaN,
      min: NaN,
      max: NaN,
      std: null,
      validPercent: 0,
      validPixelCount: 0,
      histogram: [],
      available: false,
      unavailableReason: err instanceof Error ? err.message : 'Computation failed',
    };
  }
}

/** Plain-language text report for farmers / field staff. */
export function formatDroneOrthoReportText(report: DroneOrthoFieldReport): string {
  const idx = report.indexes.find((i) => i.available) ?? report.indexes[0];
  const def = idx ? DRONE_INDEX_DEFS.find((d) => d.key === idx.key) : null;
  const p = report.parameters;

  const lines: string[] = [
    'Digisaka — Field crop health summary',
    '====================================',
    `Date: ${new Date(report.generatedAt).toLocaleString()}`,
    `Farm / field: ${report.farmName || 'Your field'}`,
    `Field size: about ${report.fieldAreaHa.toFixed(2)} hectares`,
    `Drone map: ${report.missionLabel}`,
    '',
    'What we checked',
    '---------------',
  ];

  if (idx?.available && def) {
    lines.push(`${def.plainLabel}`);
    lines.push(def.plainDescription);
    lines.push('');
    lines.push(`Overall result: ${rateIndexPlain(idx.key, idx.mean)}`);
    lines.push('On the map: green = healthier / more cover, red / yellow = weaker areas.');
    lines.push('');
    lines.push('Statistics');
    lines.push(`  Average: ${Number.isFinite(idx.mean) ? idx.mean.toFixed(3) : '—'}`);
    lines.push(`  Lowest: ${Number.isFinite(idx.min) ? idx.min.toFixed(3) : '—'}`);
    lines.push(`  Highest: ${Number.isFinite(idx.max) ? idx.max.toFixed(3) : '—'}`);
    lines.push(`  Variation (std): ${idx.std != null && Number.isFinite(idx.std) ? idx.std.toFixed(3) : '—'}`);
    lines.push(`  Coverage: ${Number.isFinite(idx.validPercent) ? idx.validPercent.toFixed(1) : '—'}%`);
  } else {
    lines.push('No crop map result was available for this field.');
  }

  lines.push('', 'Simple field insights', '---------------------');

  if (p.canopyCoverPercent != null) {
    lines.push(`Plant cover: about ${p.canopyCoverPercent.toFixed(0)}% of the field looks well covered with plants.`);
    lines.push(p.canopyCoverNote);
  } else {
    lines.push('Plant cover: not estimated for this check.');
    lines.push(p.canopyCoverNote);
  }

  lines.push('');
  lines.push(`Plant height: not available yet.`);
  lines.push(p.chmNote);

  lines.push('');
  if (p.chlorophyllProxy != null) {
    lines.push(
      `Leaf / plant greenness (${p.chlorophyllProxyLabel}): ${rateIndexPlain(
        idx?.key === 'ndre' ? 'ndre' : 'ndvi',
        p.chlorophyllProxy
      )}`
    );
  } else {
    lines.push('Leaf / plant greenness: not estimated for this check.');
  }
  lines.push(p.chlorophyllProxyNote);

  lines.push('');
  lines.push('What you can do next');
  lines.push('--------------------');
  lines.push('• Walk the red or yellow spots on the map — they may need water, fertilizer, or pest check.');
  lines.push('• Compare with how the crop looks in the field.');
  lines.push('• Ask Digisaka if you want a new drone flight for a fuller report.');
  lines.push('');
  lines.push('— Digisaka team · support@digisaka.app');
  return lines.join('\n');
}
